import to from 'await-to-js';
import pl from 'pluralize';
import { Node as DeliveryNode, EntrySys } from 'contensis-delivery-api';
import { Node, Entry } from 'contensis-management-api';

import { GetNodeOptions } from '~/providers/ContensisManagementApi';
import ContensisRepository from '~/repositories/ContensisRepository';
import mapErrorToObject from '~/transformations/mapAppErrorToObject';
import { NodeBuilder } from '~/transformations/NodeBuilder';
import { Logger } from '~/util/logger';
import { getParentPath } from '~/util/path';
import { isObject, isString } from '~/util/typeguards';

import {
  BuildNodeOptions,
  MigrateNode,
  MigrateNodesTree,
} from '~/models/Nodes';
import { firstValueOf } from '~/util/object';

class NodesService {
  private contensis: ContensisRepository;
  private source: ContensisRepository;

  migrateNodes: NodeBuilder[] = [];
  migrateNodeEntries: Entry[] = [];
  raw: DeliveryNode[] = [];
  tree?: DeliveryNode;
  ancestors: DeliveryNode[] = [];
  /**
   * Keep track of how many times we have built the migrateNodes array for accurate console messaging
   */
  timesBuilt = 0;

  get migrateEntryIds() {
    return this.migrateNodesWithEntries.map((mn) => mn.entryId || '');
  }

  get migrateNodesWithEntries() {
    return this.migrateNodes.filter((mn) => mn.entryId);
  }

  get migrateNodesTreeView() {
    // TODO: make a better considered model for MigrateNodesTree
    // or just use the original MigrateNode object with nested children
    // instead of remapping
    const remapNode = (mn: MigrateNode) => {
      if (!mn) return undefined;
      const { id, displayName, node, status, error, diff } = mn;
      const children = getChildren(mn);
      const deliveryNode: MigrateNodesTree = {
        displayName,
        id,
        slug:
          typeof node.slug === 'object'
            ? Object.values(node.slug)[0]
            : node.slug,
        includeInMenu: node.includeInMenu,
        isCanonical: node.isCanonical,
        entry: mn.entryId
          ? {
              entryTitle: '',
              sys: {
                id: mn.entryId,
                contentTypeId: mn.contentTypeId,
              } as EntrySys,
            }
          : 'entry' in mn.node
            ? mn.node.entry
            : undefined,
        children,
        childCount: children.length,
        originalId: mn.originalId,
        originalParentId: mn.parentId,
        parentId: node.parentId,
        path: mn.path,
        projectId: this.contensis.projectId,
        language: '',
        // title: displayName,
        status,
        error,
        diff,
      };
      return deliveryNode;
    };

    const getChildren = (parent: MigrateNode) => {
      const children = this.migrateNodes
        .filter((mn) => mn.node.parentId === parent.id)
        .map((mn) => remapNode(mn)) as MigrateNodesTree[];

      return children;
    };

    const root = remapNode(this.migrateNodes?.[0]);

    return root;
  }

  get rootAncestor() {
    return this.ancestors.find((n) => !n.parentId);
  }

  get rootNode() {
    return this.source.nodes.tree;
  }

  get totalCount() {
    return this.raw.length || this.DeconstructNodeTree();
  }

  _treeRootNode?: Node;
  /** There can be only one tree root node,
   * we always need to know what this is so we can
   * set the correct parentId on any nodes we map
   * at the root level in the target environment
   */
  get treeRootNode() {
    if (!this._treeRootNode) {
      this._treeRootNode =
        this.rootAncestor || (this.raw.find((n) => !n.parentId) as any);
    }
    return this._treeRootNode;
  }
  set treeRootNode(node: Node | DeliveryNode | undefined) {
    this._treeRootNode = node as any;
  }

  get treeRootPath() {
    return (
      firstValueOf(this.treeRootNode?.path) ||
      (this.treeRootNode?.slug
        ? `/${firstValueOf(this.treeRootNode.slug)}`
        : '/')
    );
  }

  constructor(contensis: ContensisRepository, source: ContensisRepository) {
    this.contensis = contensis;
    this.source = source;
  }

  GetNodes = async (
    rootPathOrOpts: GetNodeOptions,
    depth = 99
  ): Promise<[Error | null, DeliveryNode | undefined]> => {
    Logger.info(
      `[nodes] Getting nodes from ${Logger.highlightText(
        this.contensis.alias
      )} ${Logger.standardText(this.contensis.projectId)}`
    );
    const [err, tree] = await to(
      this.contensis.repo.DeliveryGetNodes({
        path: isString(rootPathOrOpts) ? rootPathOrOpts : '',
        depth,
        ...(isObject(rootPathOrOpts) ? rootPathOrOpts : {}),
      })
    );
    if (err) {
      // do something
    }
    this.tree = tree;

    this.DeconstructNodeTree();
    return [err, tree];
  };

  fetchTargetNodes = async (rootPath: string, id?: string, depth = 99) => {
    const { alias, nodes, projectId } = this.contensis;
    // Because we always need to set a parentId on a node,
    // when searching for a node by a rootPath, we will
    // resolve the parent path with children to a depth of 1
    // so we always have the correct parentId in scope if one exists
    const targetRoot = getParentPath(rootPath);

    let fetchNode = true;
    if (rootPath) {
      // Have we already resolved this root path's parent node?
      const resolved = nodes.raw.find((n) => n.path === targetRoot);
      // Have we already resolved this parent node as an ancestor of another node?
      const resolvedAncestor = nodes.ancestors.find(
        (n) => n.path === targetRoot
      );
      if (resolved || resolvedAncestor) {
        fetchNode = false;
        // We have already resolved this node previously as an ancestor of another node
        if (resolvedAncestor) nodes.raw.push(resolvedAncestor);
        return resolved || resolvedAncestor;
      }
    }

    if (fetchNode) {
      Logger.info(
        `Fetching ${rootPath ? `parent node of` : id ? `node` : `nodes at`} ${
          rootPath ? rootPath : id
        } in environment ${Logger.highlightText(
          alias
        )} project ${Logger.standardText(projectId)}`,
        !id ? true : false
      );
      // Look for nodes at the targetRoot
      // e.g. "/about/us" would need to find a parent node id at "/about"
      const [err, node] = await nodes.GetNodes(
        !rootPath && id ? { id } : targetRoot,
        depth
      );
      // if "/about" does not exist, find the next parent at "/"
      if (err) {
        if (
          rootPath &&
          'status' in err &&
          err.status === 404 &&
          targetRoot.split('/').length > 1 &&
          targetRoot !== this.treeRootPath
        ) {
          await this.fetchTargetNodes(targetRoot);
        } else throw err;
      } else if (node) {
        // Have we already resolved this node's ancestors as an ancestor of another node?
        const resolvedAncestor = nodes.ancestors.find(
          (n) =>
            n.id === node.parentId ||
            (n.path === getParentPath(targetRoot) &&
              targetRoot !== this.treeRootPath)
        );
        if (!resolvedAncestor && node.parentId)
          // If we have found nodes at the target, get the ancestors
          // as well so we can map the right parent id later
          await nodes.AppendNodeAncestors(node.id);
        return node;
      }
    }
  };

  GetNodeAncestors = async () => {
    if (this.tree?.id && this.tree.parentId)
      this.ancestors = await this.contensis.repo.DeliveryGetNodeAncestors(
        this.tree.id
      );
  };

  /**
   * Method for EntriesMigrationService to gather all ancestors of dependent nodes
   * @param id A node id to load the ancestors for
   */
  AppendNodeAncestors = async (id: string) => {
    const ancestors = await this.contensis.repo.DeliveryGetNodeAncestors(id);
    for (const ancestor of ancestors) {
      if (!this.ancestors.find((a) => a.id === ancestor.id))
        this.ancestors.push(ancestor);
    }
  };

  /**
   * Ensure we have resolved the root / home node
   * so we can set the correct parentId when creating new target nodes
   * at the root level
   */
  EnsureRootAncestor = async (depth = 0) => {
    if (!this.treeRootNode && (!this.tree?.id || this.tree?.parentId)) {
      const rootNode = await this.contensis.repo.DeliveryGetNodes({
        path: '/',
        depth,
      });
      this.tree = rootNode;
      this.DeconstructNodeTree();
      this.ancestors.push(rootNode);
    }
  };

  GetNodeEntries = async () => {
    if (this.migrateNodesWithEntries.length) {
      this.migrateNodeEntries = await this.contensis.entries.GetEntries(
        this.migrateEntryIds
      );
    }
    return this.migrateNodeEntries;
  };

  ValidateNodeEntries = async ({
    ignoreErrors,
    onError,
  }: {
    ignoreErrors: boolean;
    onError: (migrateNode: MigrateNode) => void;
  }) => {
    const entries = await this.GetNodeEntries();

    for (const migrateNode of this.migrateNodesWithEntries) {
      // Validate the entryId is available in the target, set an error if not
      if (!entries.find((e) => e.sys.id === migrateNode.entryId)) {
        // we can ignore these errors and clear the node's entryId instead
        if ('entryId' in migrateNode.node) {
          if (ignoreErrors) {
            migrateNode.node.entryId = undefined;
          } else {
            migrateNode.status = 'error';
            migrateNode.error = mapErrorToObject(
              new Error(
                `could not resolve source entry ${migrateNode.entryId} attached to ${migrateNode.path}`
              )
            );
            onError(migrateNode);
          }
        }
      }
    }
  };

  DeconstructNodeTree = (nodeTree = this.tree, keyToCount = 'id') => {
    // define the variable count to return later, set it to 0
    let count = 0;
    if (!nodeTree) return this.raw.length;
    else {
      // look through the object, if I kind the key in question increment count.
      for (const [key, value] of Object.entries(nodeTree)) {
        if (key === keyToCount) {
          count++;
          // Also push the node to the raw[] Node array, blanking out any children
          if (this.raw.findIndex((n) => n.id === value) === -1)
            this.raw.push({ ...nodeTree, children: undefined });
        }

        // if we find a nested Node go into it with my function
        if (
          (typeof value === 'object' && !Number.isNaN(Number(key))) ||
          (Array.isArray(value) && key === 'children')
        ) {
          // set count to += the return value of calling my function again. do not pass it count becouse i want count inside the recurrsion to start at 0
          // again so when it returns and adds with what I have already I get the right number.
          count += this.DeconstructNodeTree(value, keyToCount);
        }
      }
      // if the loop finds no nest objects return count.
      return count;
    }
  };

  BuildNodesToMigrate = async ({ onError }: BuildNodeOptions) => {
    const { contensis, source } = this;
    const nodes = source.nodes.raw;

    if (nodes.length) {
      Logger.info(
        `${this.timesBuilt ? `Rebuilding` : `Building`} ${pl(
          'site view node',
          nodes.length,
          true
        )} for target ${Logger.standardText(contensis.projectId)}`,
        !this.timesBuilt
      );

      // Ensure we have loaded the root siteview node so we can assign
      // the correct parentId for any root-level children we need to map
      await this.EnsureRootAncestor();
    }

    // Build the nodes for the "tree" in the source repo
    // from the root path provided e.g. "/about/us"
    for (const node of nodes) {
      const newNode = new NodeBuilder(contensis, source, node, {
        onError,
      });
      this.migrateNodes.push(newNode);
    }
    this.timesBuilt++;
  };
}

export default NodesService;
